import {
  Button,
  Flex,
  Heading,
  IconSaveLine,
  IconTrashLine,
  Spinner,
  Text,
  Tooltip,
  View,
} from "@instructure/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { ExternalService } from "../../../../../../services/ExternalService";
import { fileWriter } from "../../../../../../utils/FileUtils";
import { Card } from "../../../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../../../CustomHooks/NotificationProvider";
import { rteHasContent } from "../../../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../../../RichTextArea/RichTextViewer";
import { MeasureVersionsSelect } from "../../../../../Selectors/MeasureVersionsSelect";
import StructuredQuestionItem from "./StructuredQuestionItem";

const StructuredQuestions = ({
  measure,
  instructions,
  userEmail,
  isPreview,
  permission,
  structuredQuestions,
}) => {
  const empty_response = useMemo(() => {
    return {
      id: "",
      content: structuredQuestions.map((q) => ({
        questionId: q.id,
        content: "",
      })),
      is_draft: false,
      is_latest: false,
      author: "",
    };
  }, [structuredQuestions]);

  const { t } = useTranslation();
  const showNotification = useNotification();
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(empty_response);
  const [savingDraft, setSavingDraft] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [publishing, setPublishing] = useState();
  const [allResponsesFilled, setAllResponsesFilled] = useState(false);
  const isDraft = selectedResponse.is_draft ?? false;

  const fetchVersionHistory = useCallback(async () => {
    try {
      const res = await Promise.all(
        structuredQuestions.map((q) =>
          ExternalService.getContentVersionHistoryExternal(
            "qualitativeEvidence",
            `${measure.ddl_id}/${q.id}`,
            userEmail
          )
        )
      );

      const versionCount = res[0]?.length ?? 0;
      const versions = Array.from({ length: versionCount }, (_, vIdx) => {
        const firstEntry = res[0][vIdx];
        return {
          id: res.map((row) => row[vIdx]?.id).join(""),
          is_draft: firstEntry.is_draft,
          is_latest: firstEntry.is_latest,
          author: firstEntry.author,
          last_modified: firstEntry.last_modified,
          content: res.map((row, qIdx) => ({
            questionId: structuredQuestions[qIdx].id,
            content: row[vIdx]?.content || "",
          })),
        };
      });

      const draftEntry = versions.find((v) => v.is_draft);
      const published = versions.filter((v) => !v.is_draft);

      let currentResponse;

      if (draftEntry && !isPreview) {
        currentResponse = draftEntry;
      } else if (published.length > 0) {
        const latest = published.find((v) => v.is_latest) || published[0];
        currentResponse = latest;
      } else {
        currentResponse = empty_response;
      }

      setSelectedResponse(currentResponse);
      setAllResponsesFilled(
        currentResponse.content.every((c) => rteHasContent(c.content))
      );

      setVersions(versions);
    } catch {
      showNotification(
        t("notifications.general.fetchError", {
          obj: t("review.versionHistory"),
        }),
        ERROR
      );
      setSelectedResponse(empty_response);
      setAllResponsesFilled(false);
    } finally {
      setLoading(false);
    }
  }, [
    isPreview,
    measure.ddl_id,
    userEmail,
    showNotification,
    structuredQuestions,
    empty_response,
  ]);

  // Fetch version history for this question on mount
  useEffect(() => {
    fetchVersionHistory();
  }, [fetchVersionHistory]);

  function handleVersionSelect(versionId) {
    if (versionId === selectedResponse.id) return;
    const selected = versions.find((v) => v.id === versionId);
    if (selected) {
      setSelectedResponse(selected);
      setAllResponsesFilled(
        selected.content.every((c) => rteHasContent(c.content))
      );
    }
  }

  async function handleDeleteDraft() {
    setDeletingDraft(true);
    try {
      await Promise.all(
        structuredQuestions.map((q) => {
          const path = `${measure.ddl_id}/${q.id}/draft`;
          const metaData = { author: userEmail };

          return ExternalService.deleteFile(
            "response.html",
            "qualitativeEvidence",
            path,
            metaData
          );
        })
      );

      const newVersions = versions.filter((v) => !v.is_draft);
      const latestPublished =
        newVersions.find((v) => v.is_latest) || newVersions[0];

      if (latestPublished) {
        setSelectedResponse(latestPublished);
        setAllResponsesFilled(
          latestPublished.content.every((c) => rteHasContent(c.content))
        );
      } else {
        setSelectedResponse(empty_response);
        setAllResponsesFilled(false);
      }

      setVersions(newVersions);
      showNotification(
        t("notifications.general.deleteSuccess", { obj: t("global.draft") })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", { obj: t("global.draft") }),
        ERROR
      );
    } finally {
      setDeletingDraft(false);
    }
  }

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const draftPromises = structuredQuestions.map((q) => {
        const measureDraft = fileWriter(
          selectedResponse.content.find((c) => c.questionId === q.id).content,
          "response.html",
          "text/html"
        );
        const metaData = { author: userEmail };

        return ExternalService.saveFile(
          measureDraft,
          "qualitativeEvidence",
          `${measure.ddl_id}/${q.id}/draft`,
          metaData
        );
      });

      await Promise.all(draftPromises);

      await fetchVersionHistory();
      showNotification(
        t("notifications.general.saveSuccess", { obj: t("global.draft") })
      );
    } catch {
      showNotification(
        t("notifications.general.saveError", { obj: t("global.draft") }),
        ERROR
      );
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await Promise.all(
        structuredQuestions.map((q) => {
          const measureResponse = fileWriter(
            selectedResponse.content.find((c) => c.questionId === q.id).content,
            "response.html",
            "text/html"
          );
          const metaData = { author: userEmail };

          return ExternalService.saveFile(
            measureResponse,
            "qualitativeEvidence",
            `${measure.ddl_id}/${q.id}`,
            metaData
          );
        })
      );

      // Delete drafts after successful publish
      const hasDraft = versions.some((v) => v.is_draft);
      if (hasDraft) {
        try {
          await Promise.all(
            structuredQuestions.map((q) => {
              const path = `${measure.ddl_id}/${q.id}/draft`;
              const metaData = { author: userEmail };

              return ExternalService.deleteFile(
                "response.html",
                "qualitativeEvidence",
                path,
                metaData
              );
            })
          );
        } catch (deleteError) {
          console.warn("Failed to delete drafts after publish:", deleteError);
        }
      }

      await fetchVersionHistory();
      showNotification(
        t("notifications.assessmentResults.publishMeasureSuccess")
      );
    } catch {
      showNotification(
        t("notifications.assessmentResults.publishMeasureError"),
        ERROR
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleResponseChange = (questionId, value) => {
    const updatedContent = selectedResponse.content.map((c) =>
      c.questionId === questionId ? { ...c, content: value } : c
    );
    const newSelectedResponse = {
      ...selectedResponse,
      content: updatedContent,
    };
    setSelectedResponse(newSelectedResponse);
    setAllResponsesFilled(
      updatedContent.every((c) => rteHasContent(c.content))
    );
  };

  return (
    <Flex wrap="wrap" gap="x-small">
      <Flex.Item size="100%">
        <Flex wrap="wrap" gap="small">
          <Flex.Item>
            <Heading level="h3">{measure.name}</Heading>
          </Flex.Item>
          {!isPreview && (
            <Flex.Item shouldGrow>
              <Text fontStyle="italic">
                {t("measures.assignedTo")}: {measure.assignees}
              </Text>
            </Flex.Item>
          )}
          {!isPreview && versions.length > 0 && (
            <Flex.Item width="600px">
              <MeasureVersionsSelect
                versions={versions}
                selectedVersionId={selectedResponse.id}
                onVersionSelect={handleVersionSelect}
              />
            </Flex.Item>
          )}
        </Flex>
      </Flex.Item>
      {rteHasContent(instructions) && (
        <Flex.Item size="100%" padding="0 0 0 large">
          <View
            as="div"
            borderWidth="none none small none"
            padding="0 0 x-small 0"
          >
            <RichTextViewer
              value={instructions}
              ariaLabel={t("assessment.instructions")}
            />
          </View>
        </Flex.Item>
      )}
      {loading ? (
        <Flex.Item shouldGrow>
          <Skeleton height="10rem" count={2} style={{ marginBottom: 10 }} />
        </Flex.Item>
      ) : (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Card
            style={{
              backgroundColor: "#F2F8FA",
              border: "1px solid #ccc",
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            {structuredQuestions.map((question, index) => (
              <StructuredQuestionItem
                key={question.id}
                question={question}
                index={index}
                measureId={measure.ddl_id}
                measureAssignees={measure.assignees}
                userEmail={userEmail}
                responseText={
                  selectedResponse.content.find(
                    (c) => c.questionId === question.id
                  ).content || ""
                }
                responseId={selectedResponse.id}
                onResponseChange={handleResponseChange}
                isAssigned={permission}
                isPreview={isPreview}
                isLast={index === structuredQuestions.length - 1}
              />
            ))}
            {permission && structuredQuestions.length > 0 && (
              <Flex justifyItems="end" gap="small">
                {isDraft && (
                  <Flex.Item shouldGrow textAlign="end">
                    <Button
                      color="danger"
                      size="small"
                      onClick={handleDeleteDraft}
                      disabled={deletingDraft || savingDraft || publishing}
                      renderIcon={
                        deletingDraft ? (
                          <Spinner
                            renderTitle={t("global.pleaseWait")}
                            size="x-small"
                          />
                        ) : (
                          IconTrashLine
                        )
                      }
                    >
                      {t("global.deleteDraft")}
                    </Button>
                  </Flex.Item>
                )}
                <Flex.Item shouldGrow={!isDraft} textAlign="end">
                  <Button
                    color="secondary"
                    size="small"
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    renderIcon={
                      savingDraft ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconSaveLine
                      )
                    }
                  >
                    {isDraft ? t("global.updateDraft") : t("global.saveDraft")}
                  </Button>
                </Flex.Item>
                <Flex.Item>
                  <Tooltip
                    renderTip={t(
                      "structuredQuestions.publishRequirementMessage"
                    )}
                    on={!allResponsesFilled ? ["hover", "focus"] : []}
                  >
                    <Button
                      color="primary"
                      size="small"
                      onClick={handlePublish}
                      disabled={publishing || !allResponsesFilled}
                      renderIcon={
                        publishing ? (
                          <Spinner
                            size="x-small"
                            renderTitle={t("global.pleaseWait")}
                          />
                        ) : (
                          IconSaveLine
                        )
                      }
                    >
                      {t("results.publishMeasureResults")}
                    </Button>
                  </Tooltip>
                </Flex.Item>
              </Flex>
            )}
          </Card>
        </Flex.Item>
      )}
    </Flex>
  );
};

export default StructuredQuestions;
